"use client";

import type { CSSProperties, Dispatch, SetStateAction } from "react";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoAvatar from "@/components/shared/deso-avatar";
import { MaybeUserLabel } from "@/components/shared/maybe-user-label";
import {
  DESO_TOKEN_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
} from "@/utils/constants";
import { desoToUSD, focusToUsd } from "@/utils/currency";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

import type { AmmOrderLevel, HoverRow } from "./token-orderbook";
import { COLUMN_WIDTH, OrderDirection } from "./token-orderbook";

export const TokenOrderBookItem = ({
  data: {
    orders,
    setHoveredRow,
    defaultTransactor,
    direction,
    focusExchangeRate,
    desoExchangeRate,
    hoveredRow,
    isAsk,
    coinUsername,
    isFocus,
  },
  index,
  style,
}: {
  data: {
    orders: AmmOrderLevel[] | null;
    setHoveredRow: Dispatch<SetStateAction<HoverRow>>;
    hoveredRow: HoverRow | null;
    defaultTransactor?: PartialAccount;
    direction: OrderDirection;
    focusExchangeRate: number;
    desoExchangeRate: number;
    isAsk: boolean;
    coinUsername: string;
    isFocus: boolean;
  };
  index: number;
  style: CSSProperties;
}) => {
  const level = orders?.[index];

  if (!level) {
    return null;
  }

  const denominatingTokenDisplayName = isFocus
    ? DESO_TOKEN_DISPLAY_NAME
    : FOCUS_TOKEN_DISPLAY_NAME;
  const convertPrice = (price: number) => {
    return isFocus
      ? desoToUSD(price, desoExchangeRate)
      : focusToUsd(price, focusExchangeRate);
  };

  return (
    <div
      key={index}
      style={style}
      onMouseEnter={() =>
        setHoveredRow({
          level,
          index,
        })
      }
      onMouseLeave={() => setHoveredRow(null)}
      className={cn(
        `
          unforce-radius flex cursor-pointer items-center border-b
          transition-none
        `,
        hoveredRow &&
          hoveredRow?.index <= index &&
          direction === OrderDirection.bottomToTop &&
          "bg-card",

        hoveredRow &&
          hoveredRow?.index >= index &&
          direction === OrderDirection.topToBottom &&
          "bg-card",
      )}
    >
      <div
        className={cn(
          `
            unforce-radius flex items-center gap-1 border-l-4 border-transparent
            px-2 py-2.5
          `,
          hoveredRow &&
            isAsk &&
            (direction === OrderDirection.bottomToTop
              ? hoveredRow?.index <= index
              : hoveredRow?.index >= index) &&
            "border-red-500",
          hoveredRow &&
            !isAsk &&
            (direction === OrderDirection.topToBottom
              ? hoveredRow?.index >= index
              : hoveredRow?.index <= index) &&
            "border-green-500",
        )}
        style={{ width: `${COLUMN_WIDTH[0]}%` }}
      >
        <div className="min-h-9">
          {defaultTransactor ? (
            <div className="flex items-center gap-2">
              <DesoAvatar account={defaultTransactor} size={32} />

              <div className="flex flex-col">
                <span className="font-sans text-sm text-muted-foreground">
                  {getUserDisplayName({
                    ...defaultTransactor,
                    customPrefix: "@",
                    truncateLength: 15,
                  })}
                </span>
                <span className="text-xs text-muted">
                  {getUserName({ account: defaultTransactor })}
                </span>
              </div>
            </div>
          ) : (
            level.TransactorPublicKeyBase58Check && (
              <MaybeUserLabel
                value={level.TransactorPublicKeyBase58Check}
                showDisplayName={true}
                shortenIfNoProfile={true}
                showAvatarIfNoProfile={true}
                clickable={false}
                imageSize={32}
                userNameClassName="font-sans text-xs text-foreground"
                className="items-center"
              />
            )
          )}
        </div>
      </div>
      <div
        className={cn("font-mono", isAsk ? "text-red-500" : "text-green-500")}
        style={{ width: `${COLUMN_WIDTH[1]}%` }}
      >
        <div className="flex flex-col">
          <CoinPriceFormatter
            className="text-sm"
            price={convertPrice(level.PriceInQuoteCurrency)}
          />
          <div className="flex items-center gap-1">
            <CoinPriceFormatter
              isUsd={false}
              price={level.PriceInQuoteCurrency}
              className="text-xs text-muted"
            />{" "}
            <span className="text-xs text-muted">
              {denominatingTokenDisplayName}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col" style={{ width: `${COLUMN_WIDTH[2]}%` }}>
        <CoinPriceFormatter
          className="font-mono text-sm text-muted-foreground"
          price={level.AmountInQuoteCurrency / level.PriceInQuoteCurrency}
          isUsd={false}
        />
        <span className="text-xs text-muted">{coinUsername}</span>
      </div>
      <div className="flex flex-col" style={{ width: `${COLUMN_WIDTH[3]}%` }}>
        <CoinPriceFormatter
          className="font-mono text-sm"
          price={convertPrice(level.AmountInQuoteCurrency)}
        />
        <div className="flex items-center gap-1">
          <CoinPriceFormatter
            isUsd={false}
            price={level.AmountInQuoteCurrency}
            className="font-mono text-xs text-muted"
          />
          <span className="font-mono text-xs text-muted">
            {denominatingTokenDisplayName}
          </span>
        </div>
      </div>
    </div>
  );
};
